/**
 * The membership timeline of a rule segment — who entered, who left, and why.
 *
 * A rule segment is dynamic: its members move when an upload finishes, a Reach
 * lead lands, somebody replies, the hour turns over "in the last 30 days", or
 * the rules themselves change. Every one of those moves is an append-only row
 * (`segment_membership_events`), and this card reads the newest of them. It is
 * the concept's "Membership log", minus the three demo buttons that fed sample
 * rows into it: on a real workspace the data arrives by itself, and a button
 * labelled "Fresh upload" that uploaded nothing would be theatre.
 *
 * ## PEOPLE ARE MASKED HERE, ALWAYS
 *
 * A name and ONE masked address (`a***@bank.co.in`, `+91 ••••• ••123`), as the
 * API sends them (`maskedPersonSchema`). A timeline is the screen most often
 * read over somebody's shoulder or pasted into a chat, and nothing on it needs
 * the full address — the contacts table below it, which is a deliberate
 * click away, has those.
 *
 * ## THE TOTALS ARE OVER WHAT IS SHOWN
 *
 * "+12 entered · −3 left" is the sum over the events on screen, and the line
 * says "in the latest N" so it cannot be read as all-time. The segment's size
 * is the counts card above — computed on read — not a running sum of this log.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Spinner } from '@wend/ui';

import { ErrorState } from '../../components/ErrorState';
import { fetchMembershipEvents } from '../../lib/api/segments';
import { formatDateTime } from '../../lib/format';
import { useAsyncData } from '../../lib/use-async-data';
import { MembershipTimelineView } from './MembershipTimelineView';

/** How many of the newest events the card asks for. */
export const TIMELINE_LIMIT = 50;

export function MembershipTimeline({ segmentId, refreshedAt, refreshKey }: { segmentId: string; refreshedAt: string | null; refreshKey: string }) {
  const { data, error, loadingFirst, reload } = useAsyncData(
    (signal) => fetchMembershipEvents(segmentId, TIMELINE_LIMIT, signal),
    // A save of the rules changes `refreshKey` (the segment's updatedAt), and the log re-reads.
    [segmentId, refreshKey],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Who entered and left</CardTitle>
        <CardDescription>
          {refreshedAt === null
            ? 'Members have not been brought up to date yet.'
            : `Members were last brought up to date ${formatDateTime(refreshedAt)}.`}{' '}
          People are shown with one masked address.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {data !== null ? (
          <MembershipTimelineView events={data} />
        ) : loadingFirst ? (
          <p className="flex items-center gap-3 text-[0.9rem] text-muted">
            <Spinner size="sm" label={null} />
            <span role="status">Loading the timeline…</span>
          </p>
        ) : (
          <ErrorState error={error} title="Could not load who entered and left" onRetry={reload} className="flex flex-col items-start gap-3" />
        )}
      </CardContent>
    </Card>
  );
}
